/**
 * The contractor's own credential, for the pages that need to send it.
 *
 * WHY THIS EXISTS. lib/apiClient.js attaches a token to every call this app
 * makes, but only ever the STAFF one - it asks lib/auth's getApiToken(), which
 * is the Entra token. A contractor has no Entra token, so for them that
 * interceptor adds nothing, and any page that relies on it sends no credential
 * at all.
 *
 * Which is exactly what happened. `/my-work-orders` and `/work-orders/:id` are
 * both routed for a logged-in contractor in App.js, and WorkOrdersList links
 * one to the other - but neither page read contractor_token, so every request
 * arrived unauthenticated and the API answered 401. Two pages a contractor can
 * navigate to, from a link the app itself drew, that could not load for them.
 * The same shape as the contractor ticket page, which answered 401 for every
 * contractor until it was fixed on 8 October.
 *
 * Only four files knew to do this, each with its own copy of the same two
 * lines. Here once, so a page added later has somewhere obvious to reach for
 * and there is one place to be right.
 *
 * Returns `undefined` rather than an empty config when there is no contractor
 * token, so passing it to axios changes nothing for a member of staff - their
 * request goes through the interceptor and picks up the staff token as before.
 * A page serving both kinds of caller can therefore pass this unconditionally.
 */
import { storedViewAs } from './viewAs';

export const CONTRACTOR_TOKEN_KEY = 'contractor_token';

/** The raw token, or null. */
export function contractorToken() {
  try {
    return localStorage.getItem(CONTRACTOR_TOKEN_KEY) || null;
  } catch {
    // Private windows and blocked site data both throw rather than returning
    // null, and a page that cannot read storage is a page with no contractor
    // signed in, not a page that should fail to render.
    return null;
  }
}

/** An axios config carrying it, or undefined when there is no contractor. */
export function contractorAuth() {
  const token = contractorToken();
  return token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;
}

/** Whether a contractor is signed in on this browser. */
export const contractorSignedIn = () => Boolean(contractorToken());

/**
 * Who the signed-in contractor is, for the chrome that has to name them.
 *
 * The portal has always stashed these three alongside the token, and four
 * files read them with their own copies of the same localStorage calls. Here
 * once, wrapped, because every one of those reads throws in a private window
 * rather than returning null.
 */
export const CONTRACTOR_NAME_KEY = 'contractor_name';
export const CONTRACTOR_ID_KEY = 'contractor_id';

export function contractorIdentity() {
  if (!contractorToken()) return null;
  try {
    return {
      name: localStorage.getItem(CONTRACTOR_NAME_KEY) || 'Contractor',
      id: localStorage.getItem(CONTRACTOR_ID_KEY) || null
    };
  } catch {
    return { name: 'Contractor', id: null };
  }
}

/**
 * WHOSE PORTAL IS ON SCREEN - the contractor's own, or one an administrator
 * is looking through.
 *
 * Gordon: "when i am logged in as admin and i click on view as and i chose a
 * contractor i do not have all the contractor options in the header menu - at
 * the same time i have all the admin options there."
 *
 * Both halves of that come from the same gap. "View as" for a MEMBER OF STAFF
 * is resolved on the server - middleware/staff-auth.js swaps req.user, so
 * /auth/me answers as that person and the whole app follows. A CONTRACTOR is
 * not a users row, so there is nothing for that header to name: the server
 * keeps answering "Gordon, administrator", and every screen that asks "am I
 * an admin" says yes while every screen that asks "which contractor am I"
 * says nobody. Hence an admin navbar over a contractor's portal, and a
 * contractor's own pages refusing to load for the one person entitled to
 * everything.
 *
 * So the browser has to hold that answer, and hold it in ONE place rather
 * than in each page's own reading of localStorage. This is that place:
 *
 *   id       the contractor whose portal this is, either way
 *   name     what to call them
 *   viewing  true when an administrator is looking rather than the
 *            contractor themselves - which is what decides whether to send a
 *            portal token or let the staff token through the interceptor
 *
 * The id is read UNGATED, as ContractorDashboard already reads it and for the
 * same reason: the gated read only answers once /auth/me has confirmed the
 * administrator, which is after the first render, so a gated read here sends
 * somebody to the contractor login a moment before it would have worked. The
 * API grants nothing on the strength of it - every one of these endpoints is
 * behind optionalContractorSelf, which checks the caller really is an
 * administrator - so reading it early costs nothing.
 */
export function portalContractor() {
  const own = contractorIdentity();
  if (own?.id) return { ...own, viewing: false };
  // Through lib/viewAs rather than reading its key here: which key holds the
  // impersonation is that module's business, and a second spelling of it is
  // one to miss the day it changes.
  const seen = storedViewAs();
  if (seen && seen.kind === 'contractor' && seen.id) {
    return { id: seen.id, name: seen.name || 'Contractor', viewing: true };
  }
  return null;
}

/**
 * Sign the contractor out.
 *
 * The caller navigates; this only forgets. Kept beside the token because a
 * second copy of "which three keys make up a contractor session" is how one
 * of them gets left behind - and a stale contractor_token is not harmless: it
 * is what made an administrator's browser poll for incoming calls as the
 * contractor. See the note in components/IncomingCall.js.
 */
export function contractorSignOut() {
  for (const key of [CONTRACTOR_TOKEN_KEY, CONTRACTOR_NAME_KEY, CONTRACTOR_ID_KEY]) {
    try { localStorage.removeItem(key); } catch { /* nothing to forget */ }
  }
}
